import React, { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import maplibregl from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';
import { PageHeader, Table, Btn, Modal, Field, Select, Pager } from '../components/ui';
import { api } from '../lib/api';

interface Station {
  _id: string;
  id?: string;
  name: string;
  addr: string;
  lat: number;
  lng: number;
  pumps: number;
  ev: boolean;
  open: string;
  queue: string;
  active: boolean;
}

export default function StationsPage() {
  const qc = useQueryClient();
  const [sp, setSp] = useSearchParams();
  const search = sp.get('search') ?? '';
  const active = sp.get('active') ?? '';
  const page = parseInt(sp.get('page') ?? '1', 10);

  const { data, isLoading } = useQuery<{ items: Station[]; total: number; page: number; limit: number }>({
    queryKey: ['admin', 'stations', search, active, page],
    queryFn: () => api('/api/admin/stations', { query: { search: search || undefined, active: active || undefined, page, limit: 50 } }),
  });
  const [editing, setEditing] = useState<Partial<Station> | null>(null);

  const setFilter = (next: Partial<{ search: string; active: string; page: string }>) => {
    const merged: Record<string, string> = {};
    const cur = { search, active, page: String(page) };
    for (const k of Object.keys(cur) as (keyof typeof cur)[]) {
      const v = next[k] !== undefined ? next[k] : cur[k];
      if (v && !(k === 'page' && v === '1')) merged[k] = v as string;
    }
    setSp(merged);
  };

  const save = useMutation({
    mutationFn: async (s: Partial<Station>) => {
      if (s._id) return api(`/api/admin/stations/${s._id}`, { method: 'PATCH', body: stripPersisted(s) });
      return api('/api/admin/stations', { method: 'POST', body: stripPersisted(s) });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'stations'] });
      setEditing(null);
    },
  });

  const remove = useMutation({
    mutationFn: (id: string) => api(`/api/admin/stations/${id}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'stations'] }),
  });

  return (
    <div>
      <PageHeader kicker="ESTACIONES" title={`Red tucupet · ${data?.total ?? 0}`} sub="Cada estación tiene una ubicación, hora de apertura, bombas y cola." action={<Btn onClick={() => setEditing({ name: '', addr: '', lat: 19.4326, lng: -99.1332, pumps: 6, ev: false, open: '24h', queue: 'Sin fila', active: true })}>+ Nueva estación</Btn>} />

      <div style={{ display: 'flex', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
        <div style={{ minWidth: 260, flex: 1 }}>
          <Field label="Buscar" value={search} onChange={(v) => setFilter({ search: v, page: '1' })} placeholder="Nombre o dirección" />
        </div>
        <div style={{ minWidth: 180 }}>
          <Select<string>
            label="Estado"
            value={active}
            onChange={(v) => setFilter({ active: v, page: '1' })}
            options={[
              { value: '', label: 'Todas' },
              { value: 'true', label: 'Activas' },
              { value: 'false', label: 'Inactivas' },
            ]}
          />
        </div>
      </div>

      <Table<Station>
        rows={data?.items ?? []}
        empty={isLoading ? 'Cargando…' : 'Sin estaciones que coincidan.'}
        columns={[
          { key: 'name', label: 'Nombre', render: (r) => <span style={{ fontWeight: 600 }}>{r.name}</span> },
          { key: 'addr', label: 'Dirección', render: (r) => <span style={{ color: 'var(--text-2)' }}>{r.addr}</span> },
          { key: 'coords', label: 'Coords', render: (r) => <span className="mono" style={{ fontSize: 11 }}>{r.lat.toFixed(4)}, {r.lng.toFixed(4)}</span> },
          { key: 'pumps', label: 'Bombas', render: (r) => <span className="mono">{r.pumps}</span> },
          { key: 'open', label: 'Horario', render: (r) => r.open },
          { key: 'active', label: 'Activa', render: (r) => (r.active ? '✓' : '—') },
          {
            key: 'actions',
            label: '',
            render: (r) => (
              <div style={{ display: 'flex', gap: 6 }}>
                <Btn size="sm" variant="ghost" onClick={() => setEditing(r)}>Editar</Btn>
                <Btn size="sm" variant="danger" onClick={() => confirm('¿Desactivar?') && remove.mutate(r._id)}>Borrar</Btn>
              </div>
            ),
          },
        ]}
      />

      {data && <Pager total={data.total} page={data.page} limit={data.limit} onPage={(p) => setFilter({ page: String(p) })} />}

      <StationModal open={!!editing} initial={editing} onClose={() => setEditing(null)} onSubmit={(s) => save.mutate(s)} />
    </div>
  );
}

function stripPersisted(s: any) {
  const { _id, id, createdAt, updatedAt, __v, ...rest } = s;
  return rest;
}

function StationModal({ open, initial, onClose, onSubmit }: { open: boolean; initial: Partial<Station> | null; onClose: () => void; onSubmit: (s: Partial<Station>) => void }) {
  const [name, setName] = useState('');
  const [addr, setAddr] = useState('');
  const [lat, setLat] = useState<number>(19.4326);
  const [lng, setLng] = useState<number>(-99.1332);
  const [pumps, setPumps] = useState<number>(6);
  const [open_, setOpen_] = useState('24h');
  const [ev, setEv] = useState(false);
  const mapRef = useRef<maplibregl.Map | null>(null);
  const markerRef = useRef<maplibregl.Marker | null>(null);
  const mapContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (initial) {
      setName(initial.name ?? '');
      setAddr(initial.addr ?? '');
      setLat(initial.lat ?? 19.4326);
      setLng(initial.lng ?? -99.1332);
      setPumps(initial.pumps ?? 6);
      setOpen_(initial.open ?? '24h');
      setEv(initial.ev ?? false);
    }
  }, [initial]);

  useEffect(() => {
    if (!open || !mapContainerRef.current || mapRef.current) return;
    const map = new maplibregl.Map({
      container: mapContainerRef.current,
      style: 'https://tiles.openfreemap.org/styles/dark',
      center: [lng, lat],
      zoom: 12,
    });
    mapRef.current = map;
    map.on('load', () => {
      markerRef.current = new maplibregl.Marker({ color: '#b6f56a', draggable: true })
        .setLngLat([lng, lat])
        .addTo(map);
      markerRef.current.on('dragend', () => {
        const ll = markerRef.current!.getLngLat();
        setLat(ll.lat);
        setLng(ll.lng);
      });
    });
    return () => {
      map.remove();
      mapRef.current = null;
      markerRef.current = null;
    };
  }, [open]);

  useEffect(() => {
    if (markerRef.current) markerRef.current.setLngLat([lng, lat]);
    if (mapRef.current) mapRef.current.setCenter([lng, lat]);
  }, [lat, lng]);

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={initial?._id ? 'Editar estación' : 'Nueva estación'}
      footer={
        <>
          <Btn variant="ghost" onClick={onClose}>Cancelar</Btn>
          <Btn onClick={() => onSubmit({ ...initial, name, addr, lat, lng, pumps, open: open_, ev, active: true, queue: initial?.queue ?? 'Sin fila' })}>Guardar</Btn>
        </>
      }
    >
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <Field label="Nombre" value={name} onChange={setName} />
        <Field label="Horario" value={open_} onChange={setOpen_} placeholder="24h o 06–23" />
        <div style={{ gridColumn: '1 / -1' }}>
          <Field label="Dirección" value={addr} onChange={setAddr} fullWidth />
        </div>
        <Field label="Lat" value={lat} onChange={(v) => setLat(parseFloat(v))} type="number" step="0.0001" />
        <Field label="Lng" value={lng} onChange={(v) => setLng(parseFloat(v))} type="number" step="0.0001" />
        <Field label="Bombas" value={pumps} onChange={(v) => setPumps(parseInt(v))} type="number" />
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 24 }}>
          <input type="checkbox" checked={ev} onChange={(e) => setEv(e.target.checked)} />
          <span style={{ fontSize: 13 }}>Soporta EV</span>
        </label>
      </div>
      <div style={{ marginTop: 14 }}>
        <div className="mono" style={{ fontSize: 10, color: 'var(--text-3)', letterSpacing: '0.1em', marginBottom: 6 }}>UBICACIÓN · ARRASTRÁ EL MARCADOR</div>
        <div ref={mapContainerRef} style={{ height: 280, borderRadius: 10, overflow: 'hidden', border: '1px solid var(--border)' }} />
      </div>
    </Modal>
  );
}
